import useSWR from "swr";
import apiClient from "../../../apiClient";
import AppLayout from "../../../components/AppLayout";
import VirtualTable from "../../../components/custom/VirtualTable";
import { useRef, useState } from "react";
import moment from "moment";
import useLoading from "../../../customHooks/useLoading";
import styled from "styled-components";
import { fileDownload, formatMawb, formatPercent, print } from "../../../utils";
import GenerateReport from "./GenerateReport";
import useStationInfo from "../../../components/reporting/breakdownProgress/useStationInfo";
import useUser from "../../../customHooks/useUser";
import ActionIcon from "../../../components/custom/ActionIcon";
import Layout from "../../../components/custom/Layout";
import _ from "lodash";
import { IFFM } from "../../../globals/interfaces";

interface ExtendedFFM extends IFFM {
    progress: number;
    rackPieces: number;
}

const fetcher = async (key: string) => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        return [];
    }
}

export default function MawbBreakdown() {

    const { user } = useUser();
    const { s_unit } = user;
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const {
        data,
        isLoading,
        isValidating
    } = useSWR(`/ewr-united-mawb-breakdown?d_arrival_date=${date}&s_unit=${s_unit}`, fetcher);

    const { setLoading } = useLoading();
    setLoading(isLoading || isValidating);
    const stationInfo = useStationInfo();
    const tableRef = useRef();

    const getData = (formatData: boolean) => {
        if (tableRef.current) {
            // @ts-ignore
            const raw = tableRef.current.getResolvedState().sortedData;
            if (raw.length > 0) {
                const copy = _.cloneDeep(raw);
                const data = copy.map((record: any) => record._original);

                if (!formatData) return data;
                
                const formattedData = data.map((item: ExtendedFFM, i) => {
                    delete item.s_flight_id;
                    const formatted = {
                        '#': i + 1,
                        'Progress': formatPercent(item.progress),
                        'MAWB': formatMawb(item.s_mawb),
                        'Origin': item.s_origin,
                        'Dest': item.s_destination,
                        'Flight': item.s_flight_number,
                        'Commodity': item.s_commodity,
                        'Special Handling Code': item.s_special_handling_code,
                        'Flight PCs': item.i_actual_piece_count,
                        'Rack PCs': item.rackPieces                    
                    }
                    return formatted;
                });
                return formattedData;
            }
            return [];
        }
        return [];
    }

    const exportToCsv = () => {
        const data = getData(true);
        fileDownload(data, `MAWB Breakdown ${date}.csv`);
    }

    const printReport = () => {
        const data = getData(false);
        
        print(<GenerateReport
            data={data}
            date={date}
            stationInfo={stationInfo}
        />);
    }

    return (
        <Layout>
            <Container>
                <FlexContainer>
                    <h4>MAWB Breakdown</h4>
                    <input type={'date'} value={date} onChange={e => setDate(e.target.value)} />
                    <ActionIcon 
                        type={'print'}
                        onClick={printReport}
                    />
                    <ActionIcon 
                        type={'download'}
                        onClick={exportToCsv}
                    />
                </FlexContainer>
                <VirtualTable<ExtendedFFM>
                    data={data}
                    mapping={[{
                        name: 'Progress',
                        value: 'progress',
                        percent: true,
                        sortMethod: (a, b) => Number.parseInt(a) - Number.parseInt(b),
                        customWidth: 70
                    }, {
                        name: 'MAWB',
                        value: 's_mawb',
                        s_mawb: true
                    }, {
                        name: 'Origin',
                        value: 's_origin'
                    }, {
                        name: 'Dest',
                        value: 's_destination'
                    }, {
                        name: 'Flight',
                        value: 's_flight_number'
                    }, {
                        name: 'Commodity',
                        value: 's_commodity'
                    }, {
                        name: 'Special Handling Code',
                        value: 's_special_handling_code'
                    }, {
                        name: 'Flight PCs',
                        value: 'i_actual_piece_count',
                        number: true
                    }, {
                        name: 'Rack PCs',
                        value: 'rackPieces',
                        number: true
                    }]}
                    numRows={10}
                    reactTableRef={tableRef}
                />
            </Container>
        </Layout>
    );
}

const Container = styled.div`
    padding: 10px;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
`;